import { useEffect, useState, type FormEvent } from 'react';
import { receptionApi, type ChangeSetView, type ReceptionSettings } from '../api/client';
import { prettyNumber } from '../lib/calls';
import { plural } from '../lib/format';
import { useData } from '../lib/useData';
import { ReviewDrawer } from './ReviewDrawer';
import { Button, Card, Field, LoadError, Loading, Notice } from './ui';

/**
 * Settings → Reception (1.13.0, docs/receptionist.md): the Operators group Stitch keeps for the
 * receptionists (in step with their seats, people added by hand, ring time, where it goes when
 * nobody answers), hold reminders, Ask by message, To voicemail's mailbox prefix, the console's
 * outside numbers, and what receptionists see about coworkers.
 */
export function ReceptionSettingsCard({ domain, writable }: { domain: string; writable: boolean }) {
  const data = useData(() => receptionApi.get(domain), [domain]);
  const [form, setForm] = useState<ReceptionSettings | null>(null);
  const [notes, setNotes] = useState('');
  const [addExt, setAddExt] = useState('');
  const [outside, setOutside] = useState({ name: '', number: '' });
  const [plan, setPlan] = useState<ChangeSetView | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ tone: 'ok' | 'crit' | 'warn'; text: string } | null>(null);
  const v = data.data;
  useEffect(() => {
    if (!v) return;
    setForm(v.settings);
    setNotes(v.settings.quickNotes.join(', '));
  }, [v]);

  if (data.error) {
    return (
      <Card title="Reception">
        <div className="card-body">
          <LoadError error={data.error} retry={data.reload} />
        </div>
      </Card>
    );
  }
  if (!v || !form) {
    return (
      <Card title="Reception">
        <div className="card-body">
          <Loading label="Loading reception settings" />
        </div>
      </Card>
    );
  }
  const ops = v.operators;
  const set = (patch: Partial<ReceptionSettings>) => setForm((f) => (f ? { ...f, ...patch } : f));
  const fail = (err: unknown) => setNote({ tone: 'crit', text: err instanceof Error ? err.message : String(err) });

  /** A change to the Operators group: the plan to review, or "already so". */
  const syncOps = async (handAdded?: string[]) => {
    setBusy(true);
    setNote(null);
    try {
      const r = await receptionApi.syncOperators(domain, handAdded);
      if (r.changeSet) setPlan(r.changeSet);
      else {
        setNote({ tone: 'ok', text: 'The Operators group already matches.' });
        data.reload();
      }
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setNote(null);
    try {
      const quickNotes = notes
        .split(',')
        .map((x) => x.trim())
        .filter(Boolean)
        .slice(0, 12);
      const r = await receptionApi.save(domain, { ...form, quickNotes });
      setForm(r.settings);
      const ringChanged = r.settings.operatorsRingSeconds !== v.settings.operatorsRingSeconds || r.settings.operatorsNoAnswer !== v.settings.operatorsNoAnswer;
      setNote({ tone: 'ok', text: 'Saved.' });
      data.reload();
      // The group's ring time and no-answer destination live on the phone system: a change set.
      if (ringChanged && ops.extension) await syncOps();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const recs = v.receptionists;
  return (
    <Card title="Reception">
      <form className="card-body settings-section" onSubmit={(e) => void save(e)}>
        <p className="muted settings-lede">
          {recs.length
            ? `${plural(recs.length, 'receptionist')}: ${recs.map((r) => r.name).join(', ')}. Receptionists have the Console. Make someone one on their page in People (Seat).`
            : 'No receptionists yet. Make someone one on their page in People (Seat): they get the Console, for answering the main line and sending calls on.'}
        </p>

        <h3 className="settings-h">Operators group</h3>
        {ops.extension ? (
          <>
            <span>
              Ring group · ext {ops.extension} · kept in step with your receptionists
              {ops.members && <span className="muted"> · rings {ops.members.length ? ops.members.join(', ') : 'no one'}</span>}
            </span>
            {ops.drift ? (
              <Notice tone="warn" title="Different on the phone system">
                {ops.drift}{' '}
                <Button size="small" disabled={!writable || busy} onClick={() => void syncOps()}>
                  Fix
                </Button>
              </Notice>
            ) : (
              <span className="muted">In step with the phone system.</span>
            )}
            <ul className="plain-list">
              {ops.wanted.map((x) => {
                const rec = recs.find((r) => r.extension === x);
                const byHand = ops.handAdded.includes(x);
                return (
                  <li key={x} className="reception-member">
                    <span className="grow">
                      {rec ? rec.name : `Extension ${x}`} <span className="muted">· {x}</span>
                    </span>
                    {byHand ? (
                      <Button size="small" variant="ghost" disabled={!writable || busy} onClick={() => void syncOps(ops.handAdded.filter((h) => h !== x))}>
                        Remove
                      </Button>
                    ) : (
                      <span className="muted">Follows their seat</span>
                    )}
                  </li>
                );
              })}
            </ul>
            <div className="reception-add">
              <Field label="Add someone by hand (extension)" value={addExt} inputMode="numeric" maxLength={32} onChange={(e) => setAddExt(e.target.value.trim())} />
              <Button size="small" disabled={!writable || busy || !/^[A-Za-z0-9]{1,32}$/.test(addExt)} onClick={() => void syncOps([...ops.handAdded, addExt]).then(() => setAddExt(''))}>
                Add
              </Button>
            </div>
          </>
        ) : (
          <span className="muted">
            {ops.drift ?? 'Stitch makes a ring group named Operators when a second person becomes a receptionist, with all of them in it, and keeps it in step as seats change.'}
            {ops.drift && writable && (
              <>
                {' '}
                <Button size="small" disabled={busy} onClick={() => void syncOps()}>
                  Make it now
                </Button>
              </>
            )}
          </span>
        )}
        <div className="reception-row">
          <Field label="Ring for (seconds)" type="number" min={5} max={120} value={form.operatorsRingSeconds} disabled={!writable} onChange={(e) => set({ operatorsRingSeconds: Number(e.target.value) })} />
          <Field
            label="If nobody answers, send calls to"
            hint="An extension or number (a shared mailbox, say). Empty: the phone system decides."
            value={form.operatorsNoAnswer}
            disabled={!writable}
            onChange={(e) => set({ operatorsNoAnswer: e.target.value.trim() })}
          />
        </div>

        <h3 className="settings-h">Hold reminders</h3>
        <div className="reception-row">
          <Field label="Amber after (seconds)" type="number" min={10} max={600} value={form.holdAmberSeconds} disabled={!writable} onChange={(e) => set({ holdAmberSeconds: Number(e.target.value) })} />
          <Field label="Red after (seconds)" type="number" min={20} max={1800} value={form.holdRedSeconds} disabled={!writable} onChange={(e) => set({ holdRedSeconds: Number(e.target.value) })} />
        </div>

        <h3 className="settings-h">Blind transfers</h3>
        <label className="check-row">
          <input type="checkbox" checked={form.recallSeconds > 0} disabled={!writable} onChange={(e) => set({ recallSeconds: e.target.checked ? 30 : 0 })} />
          <span className="cell-stack">
            <span>Bring the caller back when nobody answers</span>
            <small className="muted">
              A Blind transfer to someone rings them while the caller holds, and they’re joined the moment they answer. If nobody answers in time, the caller comes back to the receptionist, still on hold. Someone whose phone sends calls to voicemail sooner gets the caller in their voicemail, as usual.
            </small>
          </span>
        </label>
        {form.recallSeconds > 0 && (
          <div className="reception-row">
            <Field label="Bring them back after (seconds)" type="number" min={10} max={120} value={form.recallSeconds} disabled={!writable} onChange={(e) => set({ recallSeconds: Number(e.target.value) })} />
          </div>
        )}

        <h3 className="settings-h">Ask by message</h3>
        <label className="check-row">
          <input type="checkbox" checked={form.askOn} disabled={!writable} onChange={(e) => set({ askOn: e.target.checked })} />
          <span className="cell-stack">
            <span>Receptionists can ask someone by message while the caller holds</span>
            <small className="muted">The person gets a Call for you card in their messages with the receptionist: Take it, Voicemail, Not now, or Give me 2 minutes.</small>
          </span>
        </label>
        {form.askOn && (
          <div className="reception-row">
            <Field label="Countdown before the receptionist chooses (seconds)" type="number" min={10} max={300} value={form.askSeconds} disabled={!writable} onChange={(e) => set({ askSeconds: Number(e.target.value) })} />
            <Field label="Quick notes (with commas between)" value={notes} disabled={!writable} onChange={(e) => setNotes(e.target.value)} />
          </div>
        )}

        <h3 className="settings-h">To voicemail</h3>
        <Field
          label="Mailbox prefix"
          hint="What your phone system dials before an extension to reach that person’s mailbox (8 + 204 is 204’s mailbox). Empty: To voicemail is off."
          value={form.voicemailPrefix}
          maxLength={4}
          inputMode="numeric"
          disabled={!writable}
          onChange={(e) => set({ voicemailPrefix: e.target.value.trim() })}
        />

        <h3 className="settings-h">Outside numbers on the console</h3>
        {form.outsideNumbers.length > 0 && (
          <ul className="plain-list">
            {form.outsideNumbers.map((o, i) => (
              <li key={`${o.number}-${i}`} className="reception-member">
                <span className="grow">
                  {o.name} <span className="muted">· {prettyNumber(o.number)}</span>
                </span>
                <Button size="small" variant="ghost" disabled={!writable} onClick={() => set({ outsideNumbers: form.outsideNumbers.filter((_, n) => n !== i) })}>
                  Remove
                </Button>
              </li>
            ))}
          </ul>
        )}
        <div className="reception-row">
          <Field label="Name" value={outside.name} maxLength={60} disabled={!writable} onChange={(e) => setOutside((o) => ({ ...o, name: e.target.value }))} />
          <Field label="Number" value={outside.number} maxLength={32} inputMode="tel" disabled={!writable} onChange={(e) => setOutside((o) => ({ ...o, number: e.target.value }))} />
          <Button
            size="small"
            disabled={!writable || !outside.name.trim() || !outside.number.trim() || form.outsideNumbers.length >= 30}
            onClick={() => {
              set({ outsideNumbers: [...form.outsideNumbers, { name: outside.name.trim(), number: outside.number.trim() }] });
              setOutside({ name: '', number: '' });
            }}
          >
            Add
          </Button>
        </div>

        <h3 className="settings-h">What receptionists see about coworkers</h3>
        <label className="check-row">
          <input type="radio" name="coworkers" checked={!form.showCoworkerDetail} disabled={!writable} onChange={() => set({ showCoworkerDetail: false })} />
          <span>That someone is on a call, and for how long</span>
        </label>
        <label className="check-row">
          <input type="radio" name="coworkers" checked={form.showCoworkerDetail} disabled={!writable} onChange={() => set({ showCoworkerDetail: true })} />
          <span>Also who they’re talking to</span>
        </label>

        {note && <Notice tone={note.tone}>{note.text}</Notice>}
        {writable && (
          <div>
            <Button type="submit" variant="primary" disabled={busy}>
              Save reception settings
            </Button>
          </div>
        )}
      </form>
      {plan && (
        <ReviewDrawer
          open
          tenant={domain}
          items={[]}
          planned={plan}
          onUnstage={() => undefined}
          onClose={() => {
            setPlan(null);
            data.reload();
          }}
        />
      )}
    </Card>
  );
}
